import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const publicAxios = axios.create({
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPrice(price) {
  return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(price);
}

function calculateTotal(maintenance) {
  // Use the backend-computed total when available (accounts for order-level discount)
  if (maintenance.total_with_vat != null) {
    return parseFloat(maintenance.total_with_vat);
  }
  const partsTotal = maintenance.parts?.reduce(
    (sum, p) => sum + parseFloat(p.line_total_incl_vat || p.line_total || 0), 0
  ) || 0;
  const jobsTotal = maintenance.jobs?.reduce(
    (sum, j) => sum + parseFloat(j.line_total_incl_vat || j.line_total || 0), 0
  ) || 0;
  return partsTotal + jobsTotal;
}

function WorkOrderConfirmationCard({ maintenance, token, onResponded }) {
  const { t } = useTranslation();
  const [responding, setResponding] = useState(false);
  const [note, setNote] = useState('');
  const [result, setResult] = useState(null);
  const [showNoteField, setShowNoteField] = useState(false);

  const handleRespond = async (action) => {
    setResponding(true);
    try {
      await publicAxios.post(
        `/api/public_maintenance/${token}/confirmation/respond/`,
        { action, note }
      );
      setResult({ success: true, action });
      if (onResponded) onResponded(action);
    } catch (err) {
      setResult({ error: err.response?.data?.error || t('Error') });
    } finally {
      setResponding(false);
    }
  };

  // Just responded in this session — show friendly thank-you message
  if (result?.success) {
    return (
      <Card
        data-testid="work-order-confirmation-result"
        sx={{
          mb: 2,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: result.action === 'confirm' ? 'success.main' : 'error.main',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {result.action === 'confirm' ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('confirmation.thankYouConfirmed')}
              </Typography>
              <Typography color="text.secondary">
                {t('confirmation.thankYouConfirmedDescription')}
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('confirmation.workOrderDeclined')}
              </Typography>
              <Typography color="text.secondary">
                {t('confirmation.workOrderDeclinedDescription')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Answered quote: the customer accepted a frozen snapshot, so link to it instead of
  // showing the live work order total, which may already differ.
  if (
    (maintenance.confirmation_status === 'confirmed' || maintenance.confirmation_status === 'declined') &&
    maintenance.confirmation_quote_token
  ) {
    const isConfirmed = maintenance.confirmation_status === 'confirmed';
    return (
      <Card
        data-testid="work-order-confirmation-result"
        sx={{
          mb: 2,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: isConfirmed ? 'success.main' : 'error.main',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          {isConfirmed ? (
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          ) : (
            <CancelIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isConfirmed ? t('confirmation.youAcceptedQuote') : t('confirmation.youDeclinedQuote')}
          </Typography>
          {maintenance.confirmation_responded_at && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {formatDateTime(maintenance.confirmation_responded_at)}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            href={`/tarjous/${maintenance.confirmation_quote_token}`}
            sx={{ mt: 2, borderRadius: '10px' }}
          >
            {t('confirmation.viewQuote')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Already confirmed/declined (from server data, e.g. on page reload)
  if (maintenance.confirmation_status === 'confirmed' || maintenance.confirmation_status === 'declined') {
    const isConfirmed = maintenance.confirmation_status === 'confirmed';
    const total = calculateTotal(maintenance);
    return (
      <Card
        data-testid="work-order-confirmation-result"
        sx={{
          mb: 2,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: isConfirmed ? 'success.main' : 'error.main',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          {isConfirmed ? (
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          ) : (
            <CancelIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isConfirmed
              ? t('confirmation.youConfirmedWorkOrder')
              : t('confirmation.youDeclinedWorkOrder')}
          </Typography>
          {maintenance.confirmation_responded_at && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {formatDateTime(maintenance.confirmation_responded_at)}
            </Typography>
          )}
          {isConfirmed && total > 0 && (
            <Typography sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>
              {t('confirmation.confirmedTotal')}: {formatPrice(total)}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  // Pending and bound to a quote snapshot: the customer answers the frozen quote on
  // its own page, never the live work order shown here.
  if (maintenance.confirmation_quote_token) {
    return (
      <Card
        data-testid="work-order-confirmation-quote-link"
        sx={{
          mb: 2,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: 'warning.main',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('quoteSnapshot.acceptTitle')}
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('confirmation.quoteAwaitingOnTracking')}
          </Typography>
          <Button
            variant="contained"
            color="success"
            fullWidth
            href={`/tarjous/${maintenance.confirmation_quote_token}`}
            sx={{ borderRadius: '10px', py: 1.5, fontWeight: 600 }}
          >
            {t('confirmation.openQuoteToRespond')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Pending — show action card
  return (
    <Card
      data-testid="work-order-confirmation-card"
      sx={{
        mb: 2,
        borderRadius: '12px',
        border: '2px solid',
        borderColor: 'warning.main',
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('confirmation.workOrderConfirmation')}
          </Typography>
        </Box>

        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('confirmation.reviewAndConfirm')}
        </Typography>

        {/* Workshop message */}
        {maintenance.confirmation_message && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('Message from workshop')}:
            </Typography>
            {maintenance.confirmation_message}
          </Alert>
        )}

        {/* Error */}
        {result?.error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
            {result.error}
          </Alert>
        )}

        {/* Optional note */}
        {!showNoteField ? (
          <Button
            variant="text"
            size="small"
            onClick={() => setShowNoteField(true)}
            sx={{ mb: 2 }}
          >
            {t('confirmation.addComment')}
          </Button>
        ) : (
          <TextField
            fullWidth
            multiline
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('confirmation.commentPlaceholder')}
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={responding ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            onClick={() => handleRespond('confirm')}
            disabled={responding}
            sx={{ flex: 1, minWidth: 140, borderRadius: '10px', py: 1.5, fontWeight: 600 }}
          >
            {t('confirmation.confirmWorkOrder')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={responding ? <CircularProgress size={18} /> : <CancelIcon />}
            onClick={() => handleRespond('decline')}
            disabled={responding}
            sx={{ flex: 1, minWidth: 140, borderRadius: '10px', py: 1.5, fontWeight: 600 }}
          >
            {t('confirmation.declineWorkOrder')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default WorkOrderConfirmationCard;
